/**
 * TranscriptionCollector — Lives inside <LiveKitRoom> to capture transcription
 * from the LiveKit transcription agent.
 *
 * Listens on three sources (priority order):
 *   1. room.registerTextStreamHandler('lk.transcription') — modern text stream API
 *   2. RoomEvent.TranscriptionReceived — native LiveKit transcription protocol
 *   3. RoomEvent.DataReceived — custom data messages with topic 'transcription'
 *
 * Interim transcription is shown immediately in gray; final text replaces it.
 * Renders nothing. Forwards transcription data to parent via callbacks.
 */

import { useEffect, useRef } from 'react';
import { useRoomContext } from '@livekit/components-react';
import { RoomEvent, type Participant } from 'livekit-client';
import type { TranscriptEntry } from '../hooks/useTranscription';

interface TranscriptionSegment {
  id: string;
  text: string;
  language: string;
  startTime: number;
  endTime: number;
  final: boolean;
  firstReceivedTime: number;
  lastReceivedTime: number;
}

interface TranscriptionCollectorProps {
  enabled: boolean;
  onSegment: (entry: TranscriptEntry) => void;
  onInterim: (text: string, speaker: string) => void;
  onListeningChange: (active: boolean) => void;
}

let _idCounter = 0;

export function TranscriptionCollector({
  enabled,
  onSegment,
  onInterim,
  onListeningChange,
}: TranscriptionCollectorProps) {
  const room = useRoomContext();

  const onSegmentRef = useRef(onSegment);
  const onInterimRef = useRef(onInterim);
  const onListeningRef = useRef(onListeningChange);

  onSegmentRef.current = onSegment;
  onInterimRef.current = onInterim;
  onListeningRef.current = onListeningChange;

  useEffect(() => {
    if (!enabled || !room) {
      onListeningRef.current(false);
      return;
    }

    onListeningRef.current(true);

    console.log('[TranscriptionCollector] Listening on room:', room.name);

    /**
     * Resolve speaker label from participant identity.
     * LiveKit identities: medico_<uid> / paciente_<uid>
     */
    function resolveSpeaker(participant?: Participant | null, fallbackIdentity?: string): string {
      const identity = participant?.identity || fallbackIdentity || '';
      if (identity.startsWith('medico_')) return 'Doctor';
      if (identity.startsWith('paciente_')) return 'Paciente';
      // Eventos acústicos clínicos detectados por el agente en el audio del
      // paciente (tos, sibilancias...). Etiqueta propia — NO 'Desconocido',
      // porque el prompt de la nota instruye ignorar a [Desconocido].
      if (identity === 'sistema') return 'Sistema';
      if (room.localParticipant && identity === room.localParticipant.identity) return 'Doctor';
      return 'Desconocido';
    }

    /** Push a finalized segment to the parent. */
    function pushFinal(text: string, speaker: string) {
      const trimmed = text.trim();
      if (!trimmed) return;
      onSegmentRef.current({
        id: ++_idCounter,
        text: trimmed,
        timestamp: new Date(),
        isFinal: true,
        speaker,
      });
      onInterimRef.current('', '');
    }

    /** Show interim (gray) text. */
    function pushInterim(text: string, speaker: string) {
      onInterimRef.current(text, speaker);
    }

    // ─── Source 1: Native Text Stream API (lk.transcription) ────────────
    // This is the modern LiveKit approach. Fires when the agent uses
    // AgentSession or STTSegmentsForwarder with text stream support.
    let textStreamRegistered = false;
    try {
      if (typeof room.registerTextStreamHandler === 'function') {
        room.registerTextStreamHandler('lk.transcription', async (reader, participantInfo) => {
          const attrs = reader.info?.attributes || {};
          const isFinal = attrs['lk.transcription_final'] === 'true';
          const segmentId = attrs['lk.segment_id'] || '';
          const message = await reader.readAll();

          console.log('[TranscriptionCollector] TextStream lk.transcription:',
            participantInfo.identity, isFinal ? 'FINAL' : 'interim',
            `seg=${segmentId}`, message.slice(0, 60));

          const speaker = resolveSpeaker(null, participantInfo.identity);

          if (isFinal) {
            pushFinal(message, speaker);
          } else {
            pushInterim(message, speaker);
          }
        });
        textStreamRegistered = true;
        console.log('[TranscriptionCollector] Registered lk.transcription text stream handler');
      }
    } catch (err) {
      console.warn('[TranscriptionCollector] registerTextStreamHandler not available:', err);
    }

    // ─── Source 2: Native TranscriptionReceived event (SDK 2.x) ─────────
    // Fires when agent uses STTSegmentsForwarder. Includes interim segments
    // (final=false) and final segments (final=true) with same segment id.
    const handleTranscription = (
      segments: TranscriptionSegment[],
      participant?: Participant,
      // _publication?: TrackPublication,
    ) => {
      console.log('[TranscriptionCollector] TranscriptionReceived:',
        participant?.identity, segments.length, 'segments',
        segments[0]?.final ? 'FINAL' : 'interim',
        segments[0]?.text?.slice(0, 60));

      const speaker = resolveSpeaker(participant);

      for (const seg of segments) {
        if (seg.final) {
          pushFinal(seg.text, speaker);
        } else {
          pushInterim(seg.text, speaker);
        }
      }
    };

    // ─── Source 3: Custom data messages (publish_data fallback) ──────────
    // Fires when agent uses publish_data with topic='transcription'.
    // Backward compatibility with agents that don't use native protocol.
    const handleData = (
      payload: Uint8Array,
      participant: unknown,
      _kind: unknown,
      topic?: string,
    ) => {
      if (topic !== 'transcription') return;

      // Skip local participant messages (prevents echo)
      const p = participant as { identity?: string } | null;
      if (p?.identity && room.localParticipant && p.identity === room.localParticipant.identity) return;

      try {
        const text = new TextDecoder().decode(payload);
        const data = JSON.parse(text) as {
          type: string;
          speaker: string;
          text: string;
          isFinal: boolean;
          id: string;
        };

        if (data.type !== 'transcription') return;

        console.log('[TranscriptionCollector] DataReceived transcription:',
          data.speaker, data.isFinal ? 'FINAL' : 'interim',
          data.text.slice(0, 60));

        const speaker = resolveSpeaker(null, data.speaker);

        if (data.isFinal) {
          pushFinal(data.text, speaker);
        } else {
          pushInterim(data.text, speaker);
        }
      } catch (err) {
        console.error('[TranscriptionCollector] Failed to parse data message:', err);
      }
    };

    room.on(RoomEvent.TranscriptionReceived, handleTranscription);
    room.on(RoomEvent.DataReceived, handleData);

    return () => {
      room.off(RoomEvent.TranscriptionReceived, handleTranscription);
      room.off(RoomEvent.DataReceived, handleData);
      if (textStreamRegistered) {
        try {
          if (typeof room.unregisterTextStreamHandler === 'function') {
            room.unregisterTextStreamHandler('lk.transcription');
          }
        } catch { /* ignore */ }
      }
      onListeningRef.current(false);
    };
  }, [enabled, room]);

  return null;
}
